import { BaseWebComponentView } from '../../../../../framework/BaseWebComponentView';
import { FULL_NAME_MAX_LENGTH, MdrContactDetailsFormViewData } from '../types';
import { GzFormFieldWebComponent } from '../../../../../ui/gzForm/formField';
import { GzClearableInputWebComponent } from '../../../../../ui/gzInput';
import { translateFn } from '../../../../../utils/translator.helper';
import { RequiredValidator } from '../../../../../ui/gzForm/validators/required.validator';
import { FormGroup } from '../../../../../ui/gzForm/formGroup/form-group';
import { GzButtonWebComponent } from '../../../../../ui/gzButton';
import { GzTextWebComponent } from '../../../../../ui/gzText';
import { EmailValidator } from '../../../../../ui/gzForm/validators/email.validator';
import { GzCheckboxWebComponent } from '../../../../../ui/gzCheckbox';
import { CustomizableValidator } from '../../../../../ui/gzForm/validators/customizable.validator';
import { RegexValidator } from '../../../../../ui/gzForm/validators/regex.validator';
import { MaxLengthValidator } from '../../../../../ui/gzForm/validators/max-length.validator';
import { Subscription } from 'rxjs';
import { RemoteValidator } from '../../../../../ui/gzForm/validators/remote.validator';
import { showErrorToast, showSuccessToast } from '@nirvana/shared/common';
import { FormValidator } from '../../../../../ui/gzForm/formField/types/form-validator';
import { GzPhoneInputWebComponent } from '../../../../../ui/gzPhoneInput';
import { PhoneNumberValidator } from '../../../../../ui/gzForm/validators/phone-number.validator';
import { PhoneNumber, PhoneNumberValidationStrings } from '../../../../../ui/gzPhoneInput/types';

export class MdrContactDetailsFormView extends BaseWebComponentView {
  public formGroup!: FormGroup;

  public dom: {
    $wrapper: HTMLElement;
    $fullNameFormField: GzFormFieldWebComponent<GzClearableInputWebComponent>;
    $emailVerificationDisclaimer: GzTextWebComponent;
    $emailAddressFormField: GzFormFieldWebComponent<GzClearableInputWebComponent>;
    $emailAddressMiniForm: HTMLElement;
    $emailVerificationCodeFormField: GzFormFieldWebComponent<GzClearableInputWebComponent>;
    $hasVerifiedEmailAddressFormField: GzFormFieldWebComponent<GzCheckboxWebComponent>;
    $sendEmailVerificationCodeButton: GzButtonWebComponent;
    $phoneNumberFormField: GzFormFieldWebComponent<GzPhoneInputWebComponent>;
    $clearDataButton: GzButtonWebComponent;
  };

  private emailAddressValidSubscription?: Subscription;
  private verifiedEmailAddresses: string[] = [];
  private validators: {
    requiredFullName?: FormValidator;
    requiredEmail?: FormValidator;
    requiredCode?: FormValidator;
    requiredPhone?: FormValidator;
    optionalPhone?: FormValidator;
  } = {};

  getDomElements() {
    this.dom = {
      $wrapper: this.shadow.querySelector('.wrapper'),
      $fullNameFormField: this.shadow.querySelector('#full-name-form-field'),
      $emailVerificationDisclaimer: this.shadow.querySelector('#email-verification-disclaimer'),
      $emailAddressFormField: this.shadow.querySelector('#email-address-form-field'),
      $emailAddressMiniForm: this.shadow.querySelector('#email-address-mini-form'),
      $emailVerificationCodeFormField: this.shadow.querySelector('#verification-code-form-field'),
      $hasVerifiedEmailAddressFormField: this.shadow.querySelector('#has-verified-email-address-form-field'),
      $sendEmailVerificationCodeButton: this.shadow.querySelector('#send-verification-code-button'),
      $phoneNumberFormField: this.shadow.querySelector('#phone-number-form-field'),
      $clearDataButton: this.shadow.querySelector('#clear-data-button'),
    };

    this.setTranslatedStrings();
  }

  setTranslatedStrings() {
    this.translations = {
      fullNameRequiredValidation: translateFn('Full name is required'),
      fullNameRegexValidation: translateFn(
        'Full name cannot start with whitespace and must not contain angle brackets (< or >)'
      ),
      fullNameMaxLengthValidation: translateFn('Full name must have maximum {fullNameMaxLength} characters', {
        fullNameMaxLength: FULL_NAME_MAX_LENGTH,
      }),
      emailRequiredValidation: translateFn('Email address is required'),
      emailFormatValidation: translateFn('Enter a valid email address'),
      emailVerificationCodeRequiredValidation: translateFn('Verification code is required'),
      emailVerificationCodeDoesNotMatchValidation: translateFn(
        'Verification code does not match the one sent via email.'
      ),
      emailVerificationCodeServerError: translateFn('A communication error has occurred. Please try again.'),
      emailVerificationCodeSentSuccessfully: translateFn('Verification email sent.'),
      emailVerificationCodeErrorLimitReached: translateFn('Email verification limit reached. Please try again later.'),
      emailVerificationCodeErrorNotSent: translateFn('Failed to send verification email.'),
      phoneNumberRequiredValidation: translateFn('The phone number and country code fields are required.'),
      phoneNumberInvalidSubscriberNumber: translateFn('Phone number must be between 3 and 14 digits.'),
      phoneNumberInvalid: translateFn('Country code and phone number combined must not exceed 15 characters.'),
    };

    this.dom.$fullNameFormField.label = translateFn('Full name');
    this.dom.$fullNameFormField.Input.placeholder = translateFn('Enter the name and surname');

    this.dom.$emailVerificationDisclaimer.innerText = translateFn(
      'You need to validate the provided email address by sending a validation code and entering it in the Verification code field. The code is valid for 30 minutes.'
    );

    this.dom.$emailAddressFormField.label = translateFn('Email address');
    this.dom.$emailAddressFormField.Input.placeholder = translateFn('Enter the email address');

    this.dom.$emailVerificationCodeFormField.label = translateFn('Verification code');
    this.dom.$emailVerificationCodeFormField.Input.placeholder = translateFn('Enter the verification code');

    this.dom.$sendEmailVerificationCodeButton.textContent = translateFn('Send a verification code');

    this.dom.$phoneNumberFormField.label = translateFn('Phone number');
    this.dom.$phoneNumberFormField.Input.strings = {
      numberPlaceholder: translateFn('Insert number here'),
      emptyCodeValuePlaceholder: translateFn('Select country code first'),
      phoneCodeLabel: translateFn('Country code'),
      label: '',
      noSearchResultsString: translateFn('No results'),
      searchPlaceholderString: translateFn('Search'),
    };

    this.dom.$clearDataButton.textContent = translateFn('Clear data');
  }

  async buildSuccessView(data: MdrContactDetailsFormViewData) {
    this.buildFullNameFormField(data);
    this.buildEmailVerificationDisclaimer(data);
    this.buildEmailAddressFormField(data);
    this.buildSendEmailVerificationCodeButton(data);
    this.buildHasVerifiedEmailAddressHiddenCheckbox(data);
    this.buildEmailVerificationCodeFormField(data);
    this.buildPhoneNumberFormField(data);
    this.buildClearDataButton(data);
    this.createFormGroup();
  }

  protected onDisconnect() {
    this.formGroup?.unsubscribe();
    this.emailAddressValidSubscription?.unsubscribe();
  }

  public setEmailVerificationDisclaimerHighlight(shouldHighlight: boolean): void {
    if (shouldHighlight) {
      this.dom.$emailVerificationDisclaimer.setAttribute('highlight', '');
    } else {
      this.dom.$emailVerificationDisclaimer.removeAttribute('highlight');
    }
  }

  public cacheVerifiedEmailAddress(emailAddress: string): void {
    this.verifiedEmailAddresses.push(emailAddress);
  }

  public updateElementsWhenHasSentEmailVerificationCodeWithSuccess(): void {
    this.setEmailVerificationDisclaimerHighlight(false);
    this.dom.$sendEmailVerificationCodeButton.disabled = true;
    this.dom.$hasVerifiedEmailAddressFormField.value = true;
    this.dom.$emailVerificationCodeFormField.disabled = false;
  }

  public updateElementsWhenHasSentEmailVerificationCodeWithError(): void {
    this.setEmailVerificationDisclaimerHighlight(true);
    this.dom.$sendEmailVerificationCodeButton.disabled = false;
    this.dom.$hasVerifiedEmailAddressFormField.value = false;
    // The verification code's read-only state remains unchanged
  }

  public showSuccessToastEmailVerificationCode(): void {
    showSuccessToast(this.translations.emailVerificationCodeSentSuccessfully);
  }

  public showErrorToastEmailVerificationCode(error: any): void {
    const exceptionType = error?.exceptionType ?? null;

    error.message =
      exceptionType === 'Exceptions\\LimitExceededException'
        ? this.translations.emailVerificationCodeErrorLimitReached
        : this.translations.emailVerificationCodeErrorNotSent;

    showErrorToast(error);
  }

  public hasAtLeastOneFormFieldFull(): boolean {
    const fullName = this.dom.$fullNameFormField.value;
    const email = this.dom.$emailAddressFormField.value;
    const emailVerificationCode = this.dom.$emailVerificationCodeFormField.value;
    const phoneNumber = this.dom.$phoneNumberFormField.value as PhoneNumber;
    const phoneNumberIsFull = phoneNumber.countryCode !== '' || phoneNumber.subscriberNumber !== '';

    return fullName !== '' || email !== '' || emailVerificationCode !== '' || phoneNumberIsFull;
  }

  public getRequiredValidatorFullName(): FormValidator {
    return (this.validators.requiredFullName ??= RequiredValidator(this.translations.fullNameRequiredValidation));
  }

  public getRequiredValidatorEmail(): FormValidator {
    return (this.validators.requiredEmail ??= RequiredValidator(this.translations.emailRequiredValidation));
  }

  public getRequiredValidatorEmailVerificationCode(): FormValidator {
    return (this.validators.requiredCode ??= RequiredValidator(
      this.translations.emailVerificationCodeRequiredValidation
    ));
  }

  public getPhoneNumberValidator(isRequired: boolean): FormValidator {
    const validationTexts: PhoneNumberValidationStrings = {
      mandatoryErrorText: this.translations.phoneNumberRequiredValidation,
      invalidInputValueErrorText: this.translations.phoneNumberInvalidSubscriberNumber,
      invalidPhoneErrorText: this.translations.phoneNumberInvalid,
    };

    const phoneNumberValidator = PhoneNumberValidator({ isRequired, validationTexts });

    if (isRequired) {
      return (this.validators.requiredPhone ??= phoneNumberValidator);
    }

    return (this.validators.optionalPhone ??= phoneNumberValidator);
  }

  private buildFullNameFormField(data: MdrContactDetailsFormViewData): void {
    this.dom.$fullNameFormField.stacked = data.hasStackedFormFields ?? false;
    this.dom.$fullNameFormField.value = data?.value?.fullName ?? '';
    this.dom.$fullNameFormField.readonly = !data?.enabled?.fullName ?? false;
    this.dom.$fullNameFormField.Validators = [
      ...(data.required ? [this.getRequiredValidatorFullName()] : []),
      RegexValidator(/^[^\s<>][^<>]*$/, this.translations.fullNameRegexValidation),
      MaxLengthValidator(FULL_NAME_MAX_LENGTH, this.translations.fullNameMaxLengthValidation),
    ];
  }

  private buildEmailVerificationDisclaimer(data: MdrContactDetailsFormViewData): void {
    const shouldHighlightDisclaimer = data?.enabled?.sendEmailVerificationCode ?? false;
    this.setEmailVerificationDisclaimerHighlight(shouldHighlightDisclaimer);
  }

  private buildEmailAddressFormField(data: MdrContactDetailsFormViewData): void {
    this.dom.$emailAddressFormField.value = data?.value?.email ?? '';
    this.dom.$emailAddressFormField.readonly = !data?.enabled?.email ?? false;
    this.dom.$emailAddressFormField.Validators = [
      ...(data.required ? [this.getRequiredValidatorEmail()] : []),
      EmailValidator(this.translations.emailFormatValidation),
    ];

    this.emailAddressValidSubscription = this.dom.$emailAddressFormField.valid$.subscribe(isValid => {
      if (this.dom.$emailAddressFormField.readonly) {
        return;
      }

      const newEmailAddress = this.dom.$emailAddressFormField.value as string;
      const emailHasBeenVerifiedBefore = this.verifiedEmailAddresses.includes(newEmailAddress);
      const shouldVerifyEmail = newEmailAddress !== '' && isValid && !emailHasBeenVerifiedBefore;

      this.setEmailVerificationDisclaimerHighlight(shouldVerifyEmail);
      this.dom.$sendEmailVerificationCodeButton.disabled = !shouldVerifyEmail;
      this.dom.$hasVerifiedEmailAddressFormField.value = emailHasBeenVerifiedBefore;
      this.dom.$emailVerificationCodeFormField.validate();
    });

    this.dom.$emailAddressFormField.disableVerticalMargin = true;
    this.dom.$emailAddressFormField.stacked = data.hasStackedFormFields ?? false;
  }

  private buildSendEmailVerificationCodeButton(data: MdrContactDetailsFormViewData): void {
    this.dom.$sendEmailVerificationCodeButton.disabled = !data?.enabled?.sendEmailVerificationCode ?? true;

    if (data.hasSendButtonBelowEmail) {
      this.dom.$emailAddressMiniForm.setAttribute('hasSendButtonBelowEmail', '');
    } else {
      this.dom.$emailAddressMiniForm.removeAttribute('hasSendButtonBelowEmail');
    }
  }

  private buildHasVerifiedEmailAddressHiddenCheckbox(data: MdrContactDetailsFormViewData): void {
    this.dom.$hasVerifiedEmailAddressFormField.value = !data?.enabled?.sendEmailVerificationCode ?? false;
    this.dom.$hasVerifiedEmailAddressFormField.Validators = [
      CustomizableValidator((value: boolean) => {
        const isValidByValue = value ? { valid: true } : { valid: false, error: '' };

        if (data.required) {
          // When MDR Form is required, the checkbox value describes its validation state
          return isValidByValue;
        }

        // When MDR Form is required dynamically, the checkbox value is used only when email address is used
        const emailAddress = this.dom.$emailAddressFormField.value as string;
        return emailAddress === '' ? { valid: true } : isValidByValue;
      }),
    ];
  }

  private buildEmailVerificationCodeFormField(data: MdrContactDetailsFormViewData): void {
    this.dom.$emailVerificationCodeFormField.value = '';
    this.dom.$emailVerificationCodeFormField.disabled = !data?.enabled?.emailVerificationCode ?? true;
    this.dom.$emailVerificationCodeFormField.stacked = data.hasStackedFormFields ?? false;
    this.dom.$emailVerificationCodeFormField.disableVerticalMargin = true;
    this.dom.$emailVerificationCodeFormField.Validators = [
      ...(data.required ? [this.getRequiredValidatorEmailVerificationCode()] : []),
      RemoteValidator(
        (emailVerificationCode: string) => {
          const emailAddress = this.dom.$emailAddressFormField.value;
          return this.dependencies.api.clients.EmailVerification.Check(emailAddress, emailVerificationCode);
        },
        (response: { success: boolean }) => response.success,
        this.translations.emailVerificationCodeDoesNotMatchValidation,
        this.translations.emailVerificationCodeServerError
      ),
    ];
  }

  private buildPhoneNumberFormField(data: MdrContactDetailsFormViewData) {
    this.dom.$phoneNumberFormField.readonly = !data?.enabled?.phoneNumber ?? false;
    this.dom.$phoneNumberFormField.stacked = data.hasStackedFormFields ?? false;
    this.dom.$phoneNumberFormField.value = {
      countryCode: data.value?.phoneNumber?.countryCode ?? '',
      subscriberNumber: data.value?.phoneNumber?.subscriberNumber ?? '',
    };
    this.dom.$phoneNumberFormField.Validators = [this.getPhoneNumberValidator(data.required ?? false)];
  }

  private buildClearDataButton(data: MdrContactDetailsFormViewData): void {
    this.dom.$clearDataButton.disabled = !data?.enabled?.clearData ?? true;
  }

  private createFormGroup(): void {
    this.formGroup = new FormGroup({
      fullName: this.dom.$fullNameFormField,
      email: this.dom.$emailAddressFormField,
      hasVerifiedEmailAddress: this.dom.$hasVerifiedEmailAddressFormField,
      emailVerificationCode: this.dom.$emailVerificationCodeFormField,
      phoneNumber: this.dom.$phoneNumberFormField,
    });
  }
}
